import { Epic, ofType } from "redux-observable";
import { RootState } from "../../store";
import * as authSelectors from "../auth/authSelectors";
import * as actions from "./userTermActions";
import { ServiceContainer } from "../../serviceContainer";
import _ from "lodash";
import { AnyAction } from "redux";
import { catchError, exhaustMap, last, map, tap } from "rxjs/operators";
import { of, Observable } from "rxjs";

export const listUserTermEpic: Epic<
  AnyAction,
  actions.GetUserTermListFailAction | actions.GetUserTermListSuccessAction,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(actions.GET_USER_TERM_LIST),
    exhaustMap((action) => {
      const state = state$.value;

      return services.apiUserTerm
        .listByOrganization(
          authSelectors.accessToken(state),
          action.organization
        )
        .pipe(
          map((x) =>
            actions.getUserTermListSuccessAction(action.organization, x.data)
          ),
          catchError(() => {
            services.toastr.error(
              "Error",
              "There was error while fetching user subscriptions"
            );
            return of(actions.getUserTermListFailAction(action.organization));
          })
        );
    })
  );

export const getUserTermEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType<actions.GetUserTermAction>(actions.GET_USER_TERM),
    exhaustMap((action) => {
      const state = state$.value;

      return services.apiUserTerm
        .get(
          authSelectors.accessToken(state),
          action.organization,
          action.userTermId
        )
        .pipe(
          map((x) =>
            actions.getUserTermSuccessAction(
              action.organization,
              action.userTermId,
              x.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Error",
              "There was error while fetching user subscriptions"
            );
            return of(
              actions.getUserTermFailAction(
                action.organization,
                action.userTermId
              )
            );
          })
        );
    })
  );

export const listUserTermVersionEpic: Epic<
  AnyAction,
  | actions.GetUserTermVersionListFailAction
  | actions.GetUserTermVersionListSuccessAction,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(actions.GET_USER_TERM_VERSION_LIST),
    exhaustMap((action) => {
      const state = state$.value;

      return services.apiUserTerm
        .listVersionsByOrganizationAndUserTerm(
          authSelectors.accessToken(state),
          action.organization,
          action.userTermId
        )
        .pipe(
          map((x) =>
            actions.getUserTermVersionListSuccessAction(
              action.organization,
              action.userTermId,
              x.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Error",
              "There was error while fetching user term versions"
            );
            return of(
              actions.getUserTermVersionListFailAction(
                action.organization,
                action.userTermId
              )
            );
          })
        );
    })
  );

export const getUserTermVersionEpic: Epic<
  AnyAction,
  | actions.GetUserTermVersionSuccessAction
  | actions.GetUserTermVersionFailAction,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(actions.GET_USER_TERM_VERSION),
    exhaustMap((action) => {
      const state = state$.value;

      return services.apiUserTerm
        .getVersion(
          authSelectors.accessToken(state),
          action.organization,
          action.userTermVersionId
        )
        .pipe(
          map((x) =>
            actions.getUserTermVersionSuccessAction(
              action.organization,
              action.userTermId,
              action.userTermVersionId,
              x.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Error",
              "There was error while fetching user term version"
            );
            return of(
              actions.getUserTermVersionFailAction(
                action.organization,
                action.userTermId,
                action.userTermVersionId
              )
            );
          })
        );
    })
  );

export const upsertUserTermEpic: Epic<
  AnyAction,
  actions.UpsertUserTermFailAction | actions.UpsertUserTermSuccessAction | any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(actions.UPSERT_USER_TERM),
    exhaustMap((action) => {
      const state = state$.value;
      const token = authSelectors.accessToken(state);
      const id = action.data?.id;
      const data = _.clone(action.data);

      let response: Observable<any>;

      if (id) {
        delete data.id;

        response = services.apiUserTerm.update(
          token,
          action.organization,
          id,
          data
        );
      } else {
        response = services.apiUserTerm.insert(
          token,
          action.organization,
          data
        );
      }

      return response.pipe(
        map((x) => actions.upsertUserTermSuccessAction(action.organization)),
        last(),
        tap(() => services.toastr.success("User term", "User term saved")),
        catchError(() => {
          services.toastr.error(
            "Error",
            "There was error while user term version"
          );
          return of(actions.upsertUserTermFailAction(action.organization));
        })
      );
    })
  );

export const upsertUserTermVersionEpic: Epic<
  AnyAction,
  | actions.UpsertUserTermVersionFailAction
  | actions.UpsertUserTermVersionSuccessAction
  | any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(actions.UPSERT_USER_TERM_VERSION),
    exhaustMap((action) => {
      const state = state$.value;
      const token = authSelectors.accessToken(state);
      const id = action.data?.id;
      const data = _.clone(action.data);

      let response: Observable<any>;

      if (id) {
        delete data.id;

        response = services.apiUserTerm.updateVersion(
          token,
          action.organization,
          id,
          data
        );
      } else {
        response = services.apiUserTerm.insertVersion(
          token,
          action.organization,
          data
        );
      }

      return response.pipe(
        map((x) =>
          actions.upsertUserTermVersionSuccessAction(action.organization)
        ),
        last(),
        tap(() =>
          services.toastr.success("Success", "User term version saved")
        ),
        catchError(() => {
          services.toastr.error(
            "Error",
            "There was error while user term version"
          );
          return of(
            actions.upsertUserTermVersionFailAction(action.organization)
          );
        })
      );
    })
  );
